import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core';
import { of, switchMap, takeUntil } from 'rxjs';

import { IApplication, SdApplicationsComponent } from '@seb/web-design-system/applications';

import {
  BrandThemesEnum, IExtendedLink, ILinkMedia, IMedia, IRichParagraph,
  PositionEnum, ThemesEnum
} from '@seb/web-design-system/core';

import { HeadingParagraphMapper, LinkMapper, ThemesMapper, transformHeadlineLevelToTitleLevel } from '../../../mappers';
import { ArgumentsLayoutTextConfigEnum, ArgumentsTextConfigEnum } from '../../../enums';
import { ComponentService, ImageService, TrackingService } from '../../../services';
import { AbstractComponent } from '../abstract-component.component';
import { WrapperComponent } from '../../../components/static/core';
import { ApplicationPushInterface } from '../../../models';

@Component({
  selector: 'wc-application-push',
  templateUrl: './application-push.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    WrapperComponent,
    SdApplicationsComponent
  ]
})
export class ApplicationPushComponent extends AbstractComponent<ApplicationPushInterface> implements OnInit {
  @Input() class = '';

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, 'wc-component is-full-width is-cms'].filter(Boolean).join(' ');
  }

  argument: IApplication;
  fullImage: IMedia;
  mainMedia: IMedia;
  themesConfig: ThemesEnum | BrandThemesEnum;
  textConfig: PositionEnum.left | PositionEnum.right;

  constructor(
    private readonly componentService: ComponentService,
    private readonly imageService: ImageService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly trackingService: TrackingService,
    private readonly headingParagraphMapper: HeadingParagraphMapper,
    private readonly linkMapper: LinkMapper
  ) {
    super();
  }

  override mandatoryFunction = (cmp: ApplicationPushInterface): boolean => {
    return !!cmp.horizontalMedia && !!cmp.verticalMedia;
  };

  ngOnInit(): void {
    this.component$
      .pipe(
        switchMap((cmp: ApplicationPushInterface) =>
          cmp.arguments?.links?.length > 0
            ? this.componentService.getSubComponentLazyComponentData(cmp, 'arguments', 'links')
            : of(cmp)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((cmp: ApplicationPushInterface) => {
        this.fullImage = cmp.horizontalMedia ? this.imageService.normalizeMedia(cmp.horizontalMedia) : undefined;
        this.mainMedia = cmp.verticalMedia ? this.imageService.normalizeMedia(cmp.verticalMedia) : undefined;
        this.textConfig = this.mapTextConfigAppPush(cmp.arguments?.textConfig);
        this.themesConfig = ThemesMapper.mapBrandTheme(cmp.theme);

        if (cmp.arguments?.media) {
          this.argument = this.toIArgumentsWithConfig(cmp);
        } else {
          this.argument = undefined;
        }

        this.setLoaded();
        this.cdRef.markForCheck();
      });
  }

  onClickLink(event: IExtendedLink | ILinkMedia): void {
    this.trackingService.sendEvent('clickdownloadApp', {
      appType: event.linkName
    });
  }

  mapLayoutVerticalTextConfig(layout: ArgumentsLayoutTextConfigEnum): PositionEnum.top | PositionEnum.bottom {
    switch (layout) {
      case ArgumentsLayoutTextConfigEnum.TOP:
        return PositionEnum.top;
      case ArgumentsLayoutTextConfigEnum.BOTTOM:
        return PositionEnum.bottom;
      default:
        return PositionEnum.top;
    }
  }

  mapTextConfigAppPush(textConfig: ArgumentsTextConfigEnum): PositionEnum.left | PositionEnum.right {
    switch (textConfig) {
      case ArgumentsTextConfigEnum.LEFT:
        return PositionEnum.left;
      case ArgumentsTextConfigEnum.RIGHT:
        return PositionEnum.right;
      default:
        return PositionEnum.left;
    }
  }

  private toIArgumentsWithConfig(cmp: ApplicationPushInterface): IApplication {
    return {
      title: cmp.verticalHeadline,
      image: this.mainMedia,
      mainMedia: this.fullImage,
      titlePosition: this.mapLayoutVerticalTextConfig(cmp.verticalHeadlinePosition),
      themesConfig: ThemesMapper.mapBrandTheme(cmp.theme),
      titleLevel: cmp.verticalHeadlineLevel ? transformHeadlineLevelToTitleLevel(cmp.verticalHeadlineLevel) : 1,
      content: this.toIRichParagraph(cmp)
    };
  }

  private toIRichParagraph(cmp: ApplicationPushInterface): IRichParagraph {
    return {
      heading: cmp.arguments?.media
        ? this.headingParagraphMapper.toHeadingParagraph(cmp.arguments)
        : undefined,
      footnote: cmp.arguments.footnote,
      links: this.linkMapper.toCustomButtons(cmp.arguments.linksObjects, this.themesConfig),
      linksImages: cmp.arguments.linksObjects
        ? cmp.arguments.linksObjects.filter((link) => !!link.medias).map((link) => this.linkMapper.toLinkMedia(link))
        : []
    };
  }
}
